// 倍率アップルーレットの効果音.
// 音声ファイルは持たず、Web Audio API でその場で合成する
// (公開ページの読み込みを増やさないため. 2026-09-29 定例の「webfont は足さない」と同じ理由).
//
// ブラウザは user の操作 (タップ) をきっかけにしないと音を出させないので、
// createLotterySound は必ず「ルーレットを回す」の onClick の中で呼ぶこと.
// なお iPhone は本体のマナーモード (消音スイッチ) 中は Web Audio の音も鳴らない.

// 回転の緩急を表す cubic-bezier の制御点 (CSS の cubic-bezier(x1, y1, x2, y2) と同じ並び).
export type SpinEasing = {
	x1: number
	y1: number
	x2: number
	y2: number
}

// カチカチ音を鳴らす間隔 (盤面の回転角, deg). 盤面の縁にピンが並んでいる想定.
const TICK_PEG_ANGLE = 45

// カチカチ音同士の最短間隔 (ms). 回り始めは速すぎて音が潰れるので間引く.
const TICK_MIN_INTERVAL_MS = 55

// 止まる直前のこの時間 (ms) はカチカチ音を鳴らさない.
// 止まった瞬間の音はファンファーレの頭と重なって濁るため.
const TICK_SILENT_TAIL_MS = 100

const MASTER_VOLUME = 0.2

const resolveAudioContextConstructor = (): typeof AudioContext | null => {
	if (typeof window === "undefined") {
		return null
	}
	if (typeof AudioContext === "function") {
		return AudioContext
	}
	// 古い Safari 用 (型は src/types/global.d.ts で宣言している).
	if (typeof window.webkitAudioContext === "function") {
		return window.webkitAudioContext
	}
	return null
}

const cubicBezierAxis = (s: number, p1: number, p2: number): number => {
	const inv = 1 - s
	return 3 * inv * inv * s * p1 + 3 * inv * s * s * p2 + s * s * s
}

// 回転が totalAngle (deg) 進む間に、ピン (TICK_PEG_ANGLE ごと) が針を通過する時刻 (ms) を返す.
// 盤面は ease-out で止まるので、音の間隔もだんだん開いていく.
const spinTickTimesMs = (
	durationMs: number,
	totalAngle: number,
	easing: SpinEasing
): number[] => {
	const samples = 2000
	const times: number[] = []
	let nextAngle = TICK_PEG_ANGLE
	let lastTime = -TICK_MIN_INTERVAL_MS
	for (let i = 0; i < samples; i++) {
		const s = i / samples
		const progress = cubicBezierAxis(s, easing.y1, easing.y2)
		const angle = progress * totalAngle
		if (angle < nextAngle) {
			continue
		}
		const time = cubicBezierAxis(s, easing.x1, easing.x2) * durationMs
		// 1 サンプルの間に複数のピンを越えることがあるので、越えた分はまとめて進める.
		while (nextAngle <= angle) {
			nextAngle += TICK_PEG_ANGLE
		}
		if (time - lastTime < TICK_MIN_INTERVAL_MS) {
			continue
		}
		if (time > durationMs - TICK_SILENT_TAIL_MS) {
			continue
		}
		times.push(time)
		lastTime = time
	}
	return times
}

export type LotterySound = {
	// 回転中のカチカチ音. 盤面の減速 (easing) に合わせて間隔が開いていく.
	playSpin: (durationMs: number, totalAngle: number, easing: SpinEasing) => void
	// 止まった時のファンファーレ. isJackpot (盤面の最大倍率) の時は音を重ねて華やかにする.
	playWin: (multiplier: number, isJackpot: boolean) => void
	// 花火が開く音. delaysMs は今から何 ms 後に開くか (画面の花火と同じ値を渡す).
	playFireworks: (delaysMs: number[]) => void
	dispose: () => void
}

// 音を鳴らせない環境 (Web Audio 非対応) では null を返す. 呼び出し側は音なしで演出を続けること.
export const createLotterySound = (): LotterySound | null => {
	const Constructor = resolveAudioContextConstructor()
	if (Constructor === null) {
		return null
	}

	let context: AudioContext
	try {
		context = new Constructor()
	} catch {
		return null
	}

	const master = context.createGain()
	master.gain.value = MASTER_VOLUME
	master.connect(context.destination)

	// 1 音を鳴らす. 立ち上がりを一瞬で上げて指数的に消すと、打楽器や鈴のような減衰になる.
	const playTone = (
		type: OscillatorType,
		frequency: number,
		startAt: number,
		durationSec: number,
		volume: number
	) => {
		const oscillator = context.createOscillator()
		const gain = context.createGain()
		oscillator.type = type
		oscillator.frequency.setValueAtTime(frequency, startAt)
		gain.gain.setValueAtTime(0.0001, startAt)
		gain.gain.exponentialRampToValueAtTime(volume, startAt + 0.008)
		gain.gain.exponentialRampToValueAtTime(0.0001, startAt + durationSec)
		oscillator.connect(gain)
		gain.connect(master)
		oscillator.start(startAt)
		oscillator.stop(startAt + durationSec + 0.02)
	}

	const playSpin = (
		durationMs: number,
		totalAngle: number,
		easing: SpinEasing
	) => {
		const requestedAt = performance.now()
		const times = spinTickTimesMs(durationMs, totalAngle, easing)

		const schedule = () => {
			// 音を出せる状態になるまでに時間がかかった分だけ、盤面は先に回っている.
			// その分を差し引いて予約し、過ぎてしまった音は鳴らさない (盤面とずれないように).
			const elapsedMs = performance.now() - requestedAt
			const startAt = context.currentTime + 0.02
			times.forEach((timeMs) => {
				const remainingMs = timeMs - elapsedMs
				if (remainingMs < 0) {
					return
				}
				// 止まりかけるほど音を低く・はっきりさせて、「どこで止まるか」の緊張感を出す.
				const progress = timeMs / durationMs
				const frequency = 1500 - 600 * progress
				const volume = 0.25 + 0.35 * progress
				playTone(
					"square",
					frequency,
					startAt + remainingMs / 1000,
					0.035,
					volume
				)
			})
		}

		if (context.state === "running") {
			schedule()
			return
		}
		// タップ直後でも suspended のままのブラウザがあるので、再開を待ってから予約する.
		context
			.resume()
			.then(schedule)
			.catch(() => undefined)
	}

	const playWin = (multiplier: number, isJackpot: boolean) => {
		const startAt = context.currentTime + 0.02

		// 駆け上がり (ド・ミ・ソ・ド).
		const runUp = [523.25, 659.25, 783.99, 1046.5]
		runUp.forEach((frequency, index) => {
			playTone("triangle", frequency, startAt + index * 0.085, 0.22, 0.7)
		})

		// 最後に和音を鳴らして伸ばす.
		const chordAt = startAt + runUp.length * 0.085
		const chord = [1046.5, 1318.51, 1567.98]
		chord.forEach((frequency) => {
			playTone("triangle", frequency, chordAt, 0.9, 0.5)
			playTone("sine", frequency * 2, chordAt, 0.7, 0.18)
		})

		// きらきら (高い音を散らす). 倍率が大きいほど数を増やす.
		const sparkleCount = 5 + multiplier * 2
		for (let i = 0; i < sparkleCount; i++) {
			const frequency = 2093 + Math.random() * 2093
			const at = chordAt + 0.05 + Math.random() * 0.7
			playTone("sine", frequency, at, 0.18, 0.22)
		}

		// 最大倍率の時だけ、もう 1 段上の音を重ねて「大当たり」にする.
		if (isJackpot) {
			playTone("triangle", 2093, chordAt + 0.16, 1.0, 0.45)
			playTone("triangle", 1567.98, chordAt + 0.16, 1.0, 0.3)
		}
	}

	const playFireworks = (delaysMs: number[]) => {
		const startAt = context.currentTime + 0.02
		delaysMs.forEach((delayMs) => {
			const at = startAt + delayMs / 1000
			// 「ドン」(低い音を素早く下げる) の後に、「パラパラ」(高い音を細かく散らす) を続ける.
			const thump = context.createOscillator()
			const thumpGain = context.createGain()
			thump.type = "sine"
			thump.frequency.setValueAtTime(190, at)
			thump.frequency.exponentialRampToValueAtTime(55, at + 0.22)
			thumpGain.gain.setValueAtTime(0.0001, at)
			thumpGain.gain.exponentialRampToValueAtTime(0.9, at + 0.01)
			thumpGain.gain.exponentialRampToValueAtTime(0.0001, at + 0.28)
			thump.connect(thumpGain)
			thumpGain.connect(master)
			thump.start(at)
			thump.stop(at + 0.3)

			for (let i = 0; i < 7; i++) {
				const frequency = 2600 + Math.random() * 2400
				playTone("square", frequency, at + 0.06 + i * 0.045, 0.04, 0.12)
			}
		})
	}

	const dispose = () => {
		// close は Promise を返すが、失敗しても画面の動きには関係ないので結果は見ない.
		context.close().catch(() => undefined)
	}

	if (context.state === "suspended") {
		context.resume().catch(() => undefined)
	}

	return { playSpin, playWin, playFireworks, dispose }
}
